export type AnalyticsProperties = Record<string, unknown>;

const PII_PROPERTY_KEYS: readonly string[] = [
    'email',
    'email_address',
    'emailAddress',
    'user_email',
    'name',
    'full_name',
    'fullName',
    'first_name',
    'firstName',
    'last_name',
    'lastName',
    'user_name',
    'userName',
    'org_name',
    'orgName',
    'company_name',
    'companyName',
    'workspace_name',
    'workspaceName',
    'phone',
    'phone_number',
    'phoneNumber',
    'address',
    'street',
    'avatar_url',
    'avatarUrl',
    'image',
    'password',
    'token',
    'apiKey',
    'api_key',
    'session_token',
    'sessionToken'
];

const URL_PROPERTY_KEYS = new Set([
    '$current_url',
    '$pathname',
    'current_url',
    'currentUrl',
    'href',
    'redirect',
    'redirect_url',
    'redirectUrl',
    'return_to',
    'returnTo',
    'url'
]);

const SENSITIVE_QUERY_KEYS = new Set([
    'access_token',
    'api_key',
    'apikey',
    'code',
    'id_token',
    'invite',
    'key',
    'magic_link',
    'magicLink',
    'password',
    'refresh_token',
    'reset_token',
    'resetToken',
    'secret',
    'session_token',
    'sessionToken',
    'state',
    'token'
]);

const SENSITIVE_ROUTE_PREFIXES: readonly string[] = [
    '/accept-invite',
    '/api/auth/callback',
    '/auth/callback',
    '/callback',
    '/extension/auth',
    '/invite/org',
    '/magic-link',
    '/oauth/callback',
    '/reset-password'
];

const TOKEN_PATH_RULES: readonly [RegExp, string][] = [
    [/^(\/invite\/org)\/[^/?#]+/i, '$1/[redacted]'],
    [/^(\/accept-invite)\/[^/?#]+/i, '$1/[redacted]'],
    [/^(\/magic-link)\/[^/?#]+/i, '$1/[redacted]'],
    [/^(\/reset-password)\/[^/?#]+/i, '$1/[redacted]'],
    [/^(\/api\/auth\/callback)\/[^/?#]+/i, '$1/[redacted]'],
    [/^(\/auth\/callback)\/[^/?#]+/i, '$1/[redacted]'],
    [/^(\/oauth\/callback)\/[^/?#]+/i, '$1/[redacted]'],
    [/^(\/callback)\/[^/?#]+/i, '$1/[redacted]']
];

const MAX_SENSITIVE_URL_DEPTH = 3;

type PostHogClientWithIdentity = NonNullable<Window['posthog']> & {
    get_distinct_id?: () => string | undefined;
    _isIdentified?: () => boolean;
};

type PostHogStub = Array<unknown> & {
    _i?: unknown[];
    __SV?: number;
    init?: (projectKey: string, options: PostHogInitOptions, name?: string) => void;
    people?: PostHogMethodQueue;
    toString?: (stub?: unknown) => string;
    [key: string]: unknown;
};

type PostHogMethodQueue = Array<unknown> & {
    people?: PostHogMethodQueue;
    toString?: (stub?: unknown) => string;
    [key: string]: unknown;
};

let hasInitialised = false;

function getClient(): PostHogClientWithIdentity | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const candidate = window.posthog;
    if (!candidate || typeof candidate !== 'object') {
        return null;
    }
    return candidate;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseUrl(value: string): URL | null {
    try {
        return new URL(value, 'https://tribetale.local');
    } catch {
        return null;
    }
}

function shouldTreatAsUrl(value: string): boolean {
    return /^(https?:)?\/\//i.test(value) || value.startsWith('/') || value.startsWith('?');
}

function containsPercentEncoding(value: string): boolean {
    return /%[0-9A-Fa-f]{2}/.test(value);
}

function tryDecodeUrlValue(value: string): string | null {
    try {
        const decoded = decodeURIComponent(value);
        return decoded === value ? null : decoded;
    } catch {
        return null;
    }
}

function redactSensitivePath(pathname: string): string {
    let redacted = pathname;
    for (const [pattern, replacement] of TOKEN_PATH_RULES) {
        redacted = redacted.replace(pattern, replacement);
    }
    return redacted;
}

function containsSensitiveRoute(value: string): boolean {
    const url = parseUrl(value);
    const pathname = url?.pathname ?? value.split(/[?#]/, 1)[0] ?? '';
    return SENSITIVE_ROUTE_PREFIXES.some(
        prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
    );
}

function shouldInspectNestedUrlValue(value: string): boolean {
    return shouldTreatAsUrl(value) || value.includes('?') || containsPercentEncoding(value);
}

function hasSensitiveNestedUrlValue(value: string, depth: number): boolean {
    if (depth > MAX_SENSITIVE_URL_DEPTH || !shouldInspectNestedUrlValue(value)) {
        return false;
    }

    const candidates = [value];
    const decoded = containsPercentEncoding(value) ? tryDecodeUrlValue(value) : null;
    if (decoded) {
        candidates.push(decoded);
    }

    for (const candidate of candidates) {
        const url = parseUrl(candidate);
        if (!url) {
            continue;
        }
        if (containsSensitiveRoute(candidate) || hasSensitiveSearch(url, depth)) {
            return true;
        }
    }
    return false;
}

function hasSensitiveSearch(url: URL, depth = 0): boolean {
    for (const [key, value] of url.searchParams.entries()) {
        if (SENSITIVE_QUERY_KEYS.has(key)) {
            return true;
        }
        if (depth < MAX_SENSITIVE_URL_DEPTH && hasSensitiveNestedUrlValue(value, depth + 1)) {
            return true;
        }
    }
    return false;
}

export function sanitizeAnalyticsUrl(value: string): string {
    const url = parseUrl(value);
    if (!url || !shouldTreatAsUrl(value)) {
        return value;
    }

    const pathname = redactSensitivePath(url.pathname);
    if (/^https?:\/\//i.test(value)) {
        return `${url.origin}${pathname}`;
    }
    if (value.startsWith('//')) {
        return `//${url.host}${pathname}`;
    }
    return `${pathname}`;
}

export function isSensitiveAnalyticsRoute(value: string): boolean {
    const url = parseUrl(value);
    if (!url || !shouldTreatAsUrl(value)) {
        return false;
    }
    return containsSensitiveRoute(value) || hasSensitiveSearch(url);
}

function sanitizeAnalyticsValue(key: string, value: unknown, seen: WeakSet<object>): unknown {
    if (typeof value === 'string') {
        return URL_PROPERTY_KEYS.has(key) || shouldTreatAsUrl(value)
            ? sanitizeAnalyticsUrl(value)
            : value;
    }

    if (Array.isArray(value)) {
        return value.map(item => sanitizeAnalyticsValue(key, item, seen));
    }

    if (value instanceof Date) {
        return value;
    }

    if (typeof URL !== 'undefined' && value instanceof URL) {
        return sanitizeAnalyticsUrl(value.toString());
    }

    if (!isObjectRecord(value)) {
        return value;
    }

    if (seen.has(value)) {
        return undefined;
    }
    seen.add(value);

    const out: AnalyticsProperties = {};
    for (const [childKey, childValue] of Object.entries(value)) {
        if (PII_PROPERTY_KEYS.includes(childKey)) {
            continue;
        }
        const sanitised = sanitizeAnalyticsValue(childKey, childValue, seen);
        if (sanitised !== undefined) {
            out[childKey] = sanitised;
        }
    }
    return out;
}

export function stripPii<T extends AnalyticsProperties | undefined>(
    properties: T
): AnalyticsProperties {
    if (!properties) {
        return {};
    }
    return sanitizeAnalyticsValue('', properties, new WeakSet<object>()) as AnalyticsProperties;
}

function sanitizeCapture(event: PostHogCapturePayload | null): PostHogCapturePayload | null {
    if (!event) {
        return event;
    }
    return {
        ...event,
        properties: stripPii(event.properties ?? {}),
        ...(event.$set ? {$set: stripPii(event.$set)} : {}),
        ...(event.$set_once ? {$set_once: stripPii(event.$set_once)} : {})
    };
}

function sanitizeNetworkRequest(data: PostHogNetworkRequest | null): PostHogNetworkRequest | null {
    if (!data || typeof data !== 'object') {
        return data;
    }

    const sanitized: PostHogNetworkRequest = {...data};
    if (typeof sanitized.name === 'string') {
        sanitized.name = sanitizeAnalyticsUrl(sanitized.name);
    }
    if (typeof sanitized.url === 'string') {
        sanitized.url = sanitizeAnalyticsUrl(sanitized.url);
    }
    delete sanitized.requestHeaders;
    delete sanitized.responseHeaders;
    delete sanitized.requestBody;
    delete sanitized.responseBody;
    return sanitized;
}

function addPostHogQueueMethod(target: PostHogMethodQueue, methodName: string): void {
    const parts = methodName.split('.');
    const methodTarget = parts.length === 2
        ? ((target[parts[0]] ||= []) as PostHogMethodQueue)
        : target;
    const queueName = parts.length === 2 ? parts[1] : methodName;

    methodTarget[queueName] = (...args: unknown[]) => {
        methodTarget.push([queueName, ...args]);
    };
}

function injectPostHogLoader(): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }

    const existing = window.posthog as PostHogStub | undefined;
    if (existing?.__SV) {
        return true;
    }

    const stub = (Array.isArray(existing) ? existing : []) as PostHogStub;
    window.posthog = stub as Window['posthog'];
    stub._i = [];
    stub.init = (projectKey: string, options: PostHogInitOptions, name?: string) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.crossOrigin = 'anonymous';
        script.async = true;
        script.src = options.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.head.appendChild(script);
        }

        let queue: PostHogMethodQueue = stub;
        let queueName = 'posthog';
        if (name !== undefined) {
            queue = [] as unknown as PostHogMethodQueue;
            stub[name] = queue;
            queueName = name;
        }
        queue.people = queue.people || ([] as unknown as PostHogMethodQueue);
        queue.toString = (includeStub?: unknown) => {
            let label = 'posthog';
            if (queueName !== 'posthog') {
                label += `.${queueName}`;
            }
            if (!includeStub) {
                label += ' (stub)';
            }
            return label;
        };
        queue.people.toString = () => `${queue.toString?.(1)}.people (stub)`;

        const methods = 'init capture register register_once unregister identify alias set_config people.set people.set_once people.unset people.increment people.append people.union people.remove people.delete_user get_distinct_id reset isFeatureEnabled onFeatureFlags reloadFeatureFlags group identifyGroup get_group_property getFeatureFlag getFeatureFlagPayload'.split(' ');
        for (const method of methods) {
            addPostHogQueueMethod(queue, method);
        }
        stub._i?.push([projectKey, options, name]);
    };
    stub.__SV = 1;
    return true;
}

export function initPostHog(): void {
    if (hasInitialised || typeof window === 'undefined') {
        return;
    }

    const key = (import.meta.env.VITE_POSTHOG_KEY ?? '').trim();
    const host = (import.meta.env.VITE_POSTHOG_HOST ?? '').trim() || 'https://posthog.codaslabs.com';
    if (!key) {
        return;
    }

    try {
        if (!injectPostHogLoader()) {
            return;
        }

        const replayDisabled = import.meta.env.VITE_POSTHOG_SESSION_REPLAY !== 'true'
            || isSensitiveAnalyticsRoute(window.location.href);

        window.posthog?.init?.(key, {
            api_host: host,
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: false,
            before_send: sanitizeCapture,
            disable_session_recording: replayDisabled,
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: '*',
                recordHeaders: false,
                recordBody: false,
                maskCapturedNetworkRequestFn: sanitizeNetworkRequest
            }
        });
        window.posthog?.register?.({product: 'tribetale'});
        hasInitialised = true;
    } catch {
        // Analytics must never break the app shell.
    }
}

export function identifyPostHog(userId: string): void {
    if (!userId) {
        return;
    }

    const client = getClient();
    if (!client) {
        return;
    }

    try {
        if (client._isIdentified?.() && client.get_distinct_id?.() !== userId) {
            client.reset?.();
        }
    } catch {
        // Analytics must never block identifying the current user.
    }

    try {
        client.identify?.(userId);
    } catch {
        // Analytics must never break the authenticated app shell.
    }
}

export function isPostHogIdentified(): boolean {
    try {
        return getClient()?._isIdentified?.() === true;
    } catch {
        return false;
    }
}

export function resetPostHog(): void {
    try {
        getClient()?.reset?.();
    } catch {
        // Analytics must never break logout.
    }
}
